import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import api from '../../../api';
import { pouchIcon, mineLureImg, dropLureImg, lureImg, superLureImg, ultraLureImg, rubyGummyImg, sapphireGummyImg, emeraldGummyImg, goldenGummyImg, diamondGummyImg } from '../../../constants/items';
import './BagItemsCard.css';

const BagItemsCard = ({ 
  mineralPotionActive, 
  mineralPotionCountdown,
  shinyPotionActive,
  shinyPotionCountdown,
  shinyCharm,
  shinySkillBonus,
  rodDurabilityBonus,
  pickaxeEfficiency,
  miningCharmStacks,
  equippedPickaxe,
  pickaxeMaxDurability,
  equippedRod,
  rodMaxDurability,
  equippedItemFinder,
  equippedCatchingGloves,
  activeLure,
  countdown: lureCountdown,
  expLureHits,
  dropLureHits,
  onOpenBag,
  onActiveGummyChange,
  spawnText,
  playerPos,
  equippedRings = [],
  onOpenHeldItemSwap,
  onOpenDepartmentStore,
  onOpenAchievements,
  onOpenDecorate,
  showDecorateButton = false
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [repelActive, setRepelActive] = useState(false);
  const [repelCountdown, setRepelCountdown] = useState(0);
  const [ringPotionActive, setRingPotionActive] = useState(false);
  const [ringPotionCountdown, setRingPotionCountdown] = useState(0);
  const [scentgHits, setScentgHits] = useState(0);
  const [activeGummy, setActiveGummy] = useState(null);
  const [gummyCountdown, setGummyCountdown] = useState(0);
  const [hasRingOfHealth, setHasRingOfHealth] = useState(false);
  const [ringOnCooldown, setRingOnCooldown] = useState(false);
  const [ringCooldownRemaining, setRingCooldownRemaining] = useState(0);
  const [healingParty, setHealingParty] = useState(false);
  const [coins, setCoins] = useState(0);
  const [leadPokemonName, setLeadPokemonName] = useState(null);
  const [coinHealLoading, setCoinHealLoading] = useState(false);

  // Check for lure status (includes scentg, repel, ring potion data)
  useEffect(() => {
    let cancelled = false;
    const checkItemStatus = async () => {
      try {
        if (document.hidden) return;
        // Check lure status for scentg (Scent of Gengar)
        const [lureResponse, gummyResponse, ringResponse, currRes, rosterRes] = await Promise.all([
          api.get("/v1/lure-status", { withCredentials: true }),
          api.get("/v1/gummy-status", { withCredentials: true }),
          api.get("/v1/ring-of-health-status", { withCredentials: true }),
          api.get("/v1/currency", { withCredentials: true }),
          api.get("/v1/roster", { withCredentials: true }),
        ]);

        if (cancelled) return;

        setScentgHits(lureResponse.data.scentg || 0);

        const gummyData = gummyResponse.data;
        
        // Find which gummy is active (has value > 0)
        let activeGummyType = null;
        let gummyTime = 0;
        
        if (gummyData.ruby_gummy > 0) {
          activeGummyType = 'ruby';
          gummyTime = gummyData.ruby_gummy;
        } else if (gummyData.sapphire_gummy > 0) {
          activeGummyType = 'sapphire';
          gummyTime = gummyData.sapphire_gummy;
        } else if (gummyData.emerald_gummy > 0) {
          activeGummyType = 'emerald';
          gummyTime = gummyData.emerald_gummy;
        } else if (gummyData.golden_gummy > 0) {
          activeGummyType = 'golden';
          gummyTime = gummyData.golden_gummy;
        } else if (gummyData.diamond_gummy > 0) {
          activeGummyType = 'diamond';
          gummyTime = gummyData.diamond_gummy;
        }
        
        setActiveGummy(activeGummyType);
        setGummyCountdown(gummyTime);
        
        // Notify parent component of active gummy change
        if (onActiveGummyChange) {
          onActiveGummyChange(activeGummyType);
        }
        
        // Check Ring of Health status
        setHasRingOfHealth(ringResponse.data.has_ring);
        setRingOnCooldown(ringResponse.data.on_cooldown);
        setRingCooldownRemaining(ringResponse.data.cooldown_remaining);

        if (Array.isArray(currRes.data)) {
          if (currRes.data.length) setCoins(Number(currRes.data[0].coins || 0));
        } else {
          setCoins(Number(currRes.data?.coins || 0));
        }

        const roster = Array.isArray(rosterRes.data) ? rosterRes.data : [];
        const lead = roster.find((p) => Number(p.slot) === 1) || roster[0] || null;
        setLeadPokemonName(lead?.name || null);
        
        // Note: Repel and Ring Potion status would need to be added to this endpoint
        // or fetched from a different endpoint if available
        // For now, keeping the state but not fetching (to avoid 404 errors)
      } catch (error) {
        console.error('Error checking lure status:', error);
      }
    };

    checkItemStatus();
    const interval = setInterval(checkItemStatus, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const handleHealPartyCoins = async () => {
    if (coinHealLoading) return;
    if (Number(coins || 0) < 100) {
      if (spawnText && playerPos) {
        spawnText(
          "Not enough coins.",
          playerPos.x,
          playerPos.y - 16,
          "red"
        );
      }
      return;
    }

    setCoinHealLoading(true);
    try {
      const response = await api.post("/v1/heal-party-coins", {}, { withCredentials: true });
      setCoins((prev) => Math.max(0, Number(prev || 0) - 100));

      if (spawnText && playerPos) {
        spawnText(
          "-100 Coins",
          playerPos.x,
          playerPos.y - 40,
          "#f44336"
        );
        spawnText(
          `Party healed! ${response.data.healed_count} Pokemon restored to full HP.`,
          playerPos.x,
          playerPos.y - 20,
          "#4ade80"
        );
      }
    } catch (error) {
      console.error('Error healing party for coins:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to heal party. Please try again.';

      if (spawnText && playerPos) {
        spawnText(
          errorMsg,
          playerPos.x,
          playerPos.y - 16,
          "red"
        );
      }
    } finally {
      setCoinHealLoading(false);
    }
  };

  // Handle heal party with Ring of Health
  const handleHealParty = async () => {
    if (ringOnCooldown || healingParty) return;
    
    setHealingParty(true);
    try {
      const response = await api.post("/v1/ring-of-health-heal", {}, { withCredentials: true });
      
      // Show success message with floating text
      if (spawnText && playerPos) {
        spawnText(
          `Party healed! ${response.data.healed_count} Pokemon restored to full HP.`,
          playerPos.x,
          playerPos.y - 20,
          '#4ade80' // green color
        );
      }
      
      // Update cooldown status
      setRingOnCooldown(true);
      setRingCooldownRemaining(300); // 5 minutes
    } catch (error) {
      console.error('Error healing party:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to heal party. Please try again.';
      
      // Show error message with floating text
      if (spawnText && playerPos) {
        spawnText(
          errorMsg,
          playerPos.x,
          playerPos.y - 20,
          '#f87171' // red color
        );
      }
    } finally {
      setHealingParty(false);
    }
  };

  // Countdown timers for repel and ring potion
  useEffect(() => {
    if (repelCountdown > 0) {
      const timer = setInterval(() => {
        setRepelCountdown(prev => {
          if (prev <= 1) {
            setRepelActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [repelCountdown]);

  useEffect(() => {
    if (ringPotionCountdown > 0) {
      const timer = setInterval(() => {
        setRingPotionCountdown(prev => {
          if (prev <= 1) {
            setRingPotionActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [ringPotionCountdown]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const baseRodMaxDurability = rodMaxDurability || equippedRod?.durability || 0;
  const effRodMaxDurability = Math.max(
    1,
    Math.floor(Number(baseRodMaxDurability || 0) * (1 + Number(rodDurabilityBonus || 0)))
  );

  const basePickaxeMaxDurability = pickaxeMaxDurability || equippedPickaxe?.durability || 0;
  const charmPreventChance = Math.max(0, Math.min(0.20, Number(miningCharmStacks || 0) * 0.02));
  const skillPreventChance = Math.max(0, Math.min(0.30, Number(pickaxeEfficiency || 0)));
  const totalPreventChance = 1 - (1 - charmPreventChance) * (1 - skillPreventChance);
  const effPickaxeMultiplier = totalPreventChance > 0 ? 1 / Math.max(1e-9, (1 - totalPreventChance)) : 1;

  // Calculate total shiny odds - show as percentage and multiplier
  const calculateShinyOdds = () => {
    // Get the number of shiny charms equipped
    const charmCount = shinyCharm?.stack_amount || 0;
    
    // Base denominator is 4096, each charm doubles the odds (divides denominator by 2)
    const denom = Math.max(1, 4096 / Math.pow(2, charmCount));
    let basePct = (1 / denom) * 100;
    
    // Add skill tree bonus (additive percentage)
    const bonusPct = (shinySkillBonus || 0) * 100;
    let finalPct = basePct + bonusPct;
    
    // Shiny potion doubles the final odds
    if (shinyPotionActive) {
      finalPct *= 2;
    }
    
    // Calculate total multiplier for display
    let totalMultiplier = Math.pow(2, charmCount);
    if (shinySkillBonus > 0) {
      totalMultiplier *= (1 + shinySkillBonus / 100);
    }
    if (shinyPotionActive) {
      totalMultiplier *= 2;
    }
    
    return `Shiny odds: ${finalPct.toFixed(2)}%`;
  };

  const hasActiveItems = mineralPotionActive || shinyPotionActive || activeLure || repelActive || ringPotionActive || shinyCharm || shinySkillBonus > 0 || expLureHits > 0 || dropLureHits > 0 || scentgHits > 0 || activeGummy || hasRingOfHealth || equippedRings.length > 0;

  // Always show the card (even if no active items) so user can access bag
  // if (!hasActiveItems) return null;

  return (
    <div className={`bag-items-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div
        className="bag-items-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="bag-items-header-left">
          <span className="bag-toggle">{isExpanded ? '▶' : '◀'}</span>
        </div>

        <div
          className="bag-open-heal-party"
          onClick={(e) => {
            e.stopPropagation();
            if (coinHealLoading || Number(coins || 0) < 100) return;
            handleHealPartyCoins();
          }}
          title={coinHealLoading ? "Healing..." : "Heal Party (100 coins)"}
          style={{
            background: 'rgba(34, 197, 94, 0.06)',
            border: '1px solid rgba(34, 197, 94, 0.9)',
            padding: '6px',
            borderRadius: '6px',
            cursor: coinHealLoading || Number(coins || 0) < 100 ? 'not-allowed' : 'pointer',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: coinHealLoading || Number(coins || 0) < 100 ? 0.55 : 1,
            pointerEvents: coinHealLoading || Number(coins || 0) < 100 ? 'none' : 'auto'
          }}
        >
          <img
            src={leadPokemonName ? `/sprites/${leadPokemonName}.png` : "/sprites/Pikachu.png"}
            alt="Heal Party"
            style={{ width: '18px', height: '18px', imageRendering: 'pixelated' }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/sprites/Pikachu.png";
            }}
          />
        </div>

        <div
          className="bag-open-achievements"
          onClick={(e) => {
            e.stopPropagation();
            if (typeof onOpenAchievements === "function") {
              onOpenAchievements();
              return;
            }
            navigate("/achievements");
          }}
          style={{
            background: 'rgba(234, 178, 8, 0.04)',
            border: '1px solid rgba(234, 179, 8, 0.95)',
            padding: '6px',
            borderRadius: '6px',
            cursor: 'pointer',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <img src="/GroundBadge.png" alt="Achievements" style={{ width: '18px', height: '18px' }} />
        </div>
        {onOpenBag && (
          <div
            className="bag-open-bag"
            onClick={(e) => {
              e.stopPropagation();
              onOpenBag();
            }}
            style={{
              background: 'rgba(74, 108, 222, 0.8)',
              border: '1px solid rgba(74, 108, 222, 0.95)',
              padding: '6px',
              borderRadius: '6px',
              cursor: 'pointer',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img src={pouchIcon} alt="Open Bag" style={{ width: '18px', height: '18px' }} />
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="bag-items-content">
          {(equippedPickaxe || equippedRod) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '6px' }}>
              {equippedPickaxe && (
                <div
                  className="bag-item"
                  style={{
                    background: 'rgba(0, 0, 0, 0.35)',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    border: '1px solid rgba(255,255,255,0.12)'
                  }}
                >
                  <img
                    src={`/${equippedPickaxe.item_image}`}
                    alt={equippedPickaxe.item_name}
                    style={{ width: '20px', height: '20px', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '11px', color: '#fff', fontWeight: 700, lineHeight: 1.1 }}>
                      Pickaxe: {equippedPickaxe.durability}/{pickaxeMaxDurability || equippedPickaxe.durability}
                    </div>
                    {(Number(pickaxeEfficiency || 0) > 0 || Number(miningCharmStacks || 0) > 0) && (
                      <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.75)', marginTop: '2px', lineHeight: 1.1 }}>
                        {Number(miningCharmStacks || 0) > 0 ? `Charm: ${Math.max(0, Math.min(10, Number(miningCharmStacks || 0)))}x (+${Math.round(charmPreventChance * 100)}% save)` : ""}
                        {Number(miningCharmStacks || 0) > 0 && Number(pickaxeEfficiency || 0) > 0 ? " • " : ""}
                        {Number(pickaxeEfficiency || 0) > 0 ? `Skill: +${Math.round(skillPreventChance * 100)}% save` : ""}
                        {` • Total ~${effPickaxeMultiplier.toFixed(2)}x duration`}
                      </div>
                    )}
                    <div style={{ height: '6px', background: 'rgba(255,255,255,0.12)', borderRadius: '999px', marginTop: '4px', overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${Math.min(
                            100,
                            ((equippedPickaxe.durability || 0) / Math.max(1, pickaxeMaxDurability || equippedPickaxe.durability || 1)) * 100
                          )}%`,
                          background: 'rgba(74, 222, 128, 0.8)'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {equippedRod && (
                <div
                  className="bag-item"
                  style={{
                    background: 'rgba(0, 0, 0, 0.35)',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    border: '1px solid rgba(255,255,255,0.12)'
                  }}
                >
                  <img
                    src={`/${equippedRod.item_image}`}
                    alt={equippedRod.item_name}
                    style={{ width: '20px', height: '20px', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '11px', color: '#fff', fontWeight: 700, lineHeight: 1.1 }}>
                      Rod: {equippedRod.durability}/{rodMaxDurability || equippedRod.durability}
                    </div>
                    {Number(rodDurabilityBonus || 0) > 0 && baseRodMaxDurability > 0 && (
                      <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.75)', marginTop: '2px', lineHeight: 1.1 }}>
                        Duration: +{Math.round(Number(rodDurabilityBonus || 0) * 100)}% ({baseRodMaxDurability} → {effRodMaxDurability})
                      </div>
                    )}
                    <div style={{ height: '6px', background: 'rgba(255,255,255,0.12)', borderRadius: '999px', marginTop: '4px', overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${Math.min(
                            100,
                            ((equippedRod.durability || 0) / Math.max(1, rodMaxDurability || equippedRod.durability || 1)) * 100
                          )}%`,
                          background: 'rgba(96, 165, 250, 0.85)'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {(equippedItemFinder || (equippedCatchingGloves && equippedCatchingGloves.stack_amount > 0)) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '6px' }}>
              {equippedItemFinder && (
                <div
                  className="bag-item"
                  style={{
                    background: 'rgba(0, 0, 0, 0.35)',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    border: '1px solid rgba(255,255,255,0.12)'
                  }}
                >
                  <img
                    src={equippedItemFinder?.item_image ? `/${equippedItemFinder.item_image}` : '/items/pokeball.png'}
                    alt={equippedItemFinder?.item_name || 'Item Finder'}
                    style={{ width: '20px', height: '20px', flexShrink: 0 }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/items/pokeball.png';
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '11px', color: '#fff', fontWeight: 700, lineHeight: 1.1 }}>
                      Item Finder Equipped
                    </div>
                  </div>
                </div>
              )}

              {equippedCatchingGloves && equippedCatchingGloves.stack_amount > 0 && (
                <div
                  className="bag-item"
                  style={{
                    background: 'rgba(0, 0, 0, 0.35)',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    border: '1px solid rgba(255,255,255,0.12)'
                  }}
                >
                  <img
                    src={equippedCatchingGloves?.item_image ? `/${equippedCatchingGloves.item_image}` : '/items/catchinggloves.png'}
                    alt={equippedCatchingGloves?.item_name || 'Catching Gloves'}
                    style={{ width: '20px', height: '20px', flexShrink: 0 }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/items/catchinggloves.png';
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '11px', color: '#fff', fontWeight: 700, lineHeight: 1.1 }}>
                      Catching Gloves Equipped
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Shiny Odds Display - show if any bonus exists */}
          {(shinyCharm || shinyPotionActive || shinySkillBonus > 0) && (
            <div 
              className="bag-item shiny-odds"
              style={{
                background: 'rgba(0,0,0,0.6)',
                padding: '6px 8px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                border: '1px solid rgba(255, 215, 0, 0.3)'
              }}
            >
              {shinyCharm && (
                <img 
                  src="/items/shinycharm.png" 
                  alt="Shiny Charm" 
                  style={{ width: '20px', height: '20px', flexShrink: 0 }}
                />
              )}
              <span style={{ fontSize: '12px', color: '#fff', fontWeight: 600, flex: 1 }}>
                {calculateShinyOdds()}
              </span>
            </div>
          )}

          {/* Shiny Potion */}
          {shinyPotionActive && (
            <div className="bag-item active-potion">
              <img src="/items/ShinyPotion.png" alt="Shiny Potion" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-timer">{formatTime(shinyPotionCountdown)}</div>
              </div>
            </div>
          )}

          {/* Mineral Potion */}
          {mineralPotionActive && (
            <div className="bag-item active-potion">
              <img src="/items/MineralPotion.png" alt="Mineral Potion" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-timer">{formatTime(mineralPotionCountdown)}</div>
              </div>
            </div>
          )}

          {/* Active Lure */}
          {activeLure && (
            <div className="bag-item active-lure">
              <img 
                src={activeLure === 'lure' ? lureImg : activeLure === 'super_lure' ? superLureImg : ultraLureImg} 
                alt="Lure" 
                style={{ width: '20px', height: '20px', flexShrink: 0 }} 
              />
              <div className="item-info">
                <div className="item-timer">{formatTime(lureCountdown)}</div>
              </div>
            </div>
          )}

          {/* Repel */}
          {repelActive && (
            <div className="bag-item active-repel">
              <img src="/items/repel.png" alt="Repel" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-timer">{formatTime(repelCountdown)}</div>
              </div>
            </div>
          )}

          {/* Ring Potion */}
          {ringPotionActive && (
            <div className="bag-item active-ring">
              <img src="/items/ringpotion.png" alt="Ring Potion" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-timer">{formatTime(ringPotionCountdown)}</div>
              </div>
            </div>
          )}

          {/* Mining XP Lure */}
          {expLureHits > 0 && (
            <div className="bag-item active-lure">
              <img src={mineLureImg} alt="Mining Lure" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-value">{expLureHits} hits</div>
              </div>
            </div>
          )}

          {/* Drop Rate Lure */}
          {dropLureHits > 0 && (
            <div className="bag-item active-lure">
              <img src={dropLureImg} alt="Drop Lure" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-value">{dropLureHits} hits</div>
              </div>
            </div>
          )}

          {/* Scent of Gengar */}
          {scentgHits > 0 && (
            <div className="bag-item active-scent">
              <img src="/items/scentofgengar.png" alt="Scent of Gengar" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <div className="item-value">{scentgHits} attempts</div>
              </div>
            </div>
          )}

          {/* Active Gummy */}
          {activeGummy && (
            <div className="bag-item active-gummy">
              <img 
                src={activeGummy === 'ruby' ? rubyGummyImg : 
                     activeGummy === 'sapphire' ? sapphireGummyImg : 
                     activeGummy === 'emerald' ? emeraldGummyImg : 
                     activeGummy === 'golden' ? goldenGummyImg : diamondGummyImg} 
                alt={`${activeGummy} Gummy`} 
                style={{ width: '20px', height: '20px', flexShrink: 0 }} 
              />
              <div className="item-info">
                <div className="item-value">{gummyCountdown} {gummyCountdown === 1 ? 'Battle' : 'Battles'}</div>
              </div>
            </div>
          )}

          {/* Ring of Health */}
          {hasRingOfHealth && (
            <div className="bag-item active-ring-health">
              <img src="/items/ringofhealth.png" alt="Ring of Health" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
              <div className="item-info">
                <button
                  onClick={handleHealParty}
                  disabled={ringOnCooldown || healingParty}
                  style={{
                    background: ringOnCooldown ? 'rgba(128, 128, 128, 0.5)' : 'rgba(76, 175, 80, 0.8)',
                    border: '1px solid rgba(76, 175, 80, 0.95)',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    cursor: ringOnCooldown || healingParty ? 'not-allowed' : 'pointer',
                    fontSize: '11px',
                    fontWeight: 600,
                    opacity: ringOnCooldown || healingParty ? 0.6 : 1
                  }}
                >
                  {healingParty ? 'Healing...' : ringOnCooldown ? formatTime(ringCooldownRemaining) : 'Heal Party'}
                </button>
              </div>
            </div>
          )}

          {/* Equipped Rings */}
          {equippedRings.map((ring) => {
            // Ring configurations
            const ringConfig = {
              28: { // Ring of Equipment
                image: '/items/ringofequipment.png',
                name: 'Ring of Equipment',
                action: onOpenHeldItemSwap,
                buttonText: 'Open',
                buttonColor: 'rgba(255, 193, 7, 0.8)',
                borderColor: 'rgba(255, 193, 7, 0.95)',
                bgColor: 'rgba(255, 193, 7, 0.15)',
                borderColorCSS: 'rgba(255, 193, 7, 0.3)'
              },
              140: { // Ring of Department
                image: '/items/ringofdepartment.png',
                name: 'Ring of Department',
                action: onOpenDepartmentStore,
                buttonText: 'Open',
                buttonColor: 'rgba(255, 215, 0, 0.8)',
                borderColor: 'rgba(255, 215, 0, 0.95)',
                bgColor: 'rgba(255, 215, 0, 0.15)',
                borderColorCSS: 'rgba(255, 215, 0, 0.3)'
              },
              161: { // Ring of Health (already handled separately above)
                skip: true
              }
            };
            
            const config = ringConfig[ring.item];
            if (!config || config.skip) return null;
            
            return (
              <div key={ring.item} className="bag-item" style={{ background: config.bgColor, borderColor: config.borderColorCSS }}>
                <img src={config.image} alt={config.name} style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                <div className="item-info">
                  <button
                    onClick={config.action}
                    style={{
                      background: config.buttonColor,
                      border: `1px solid ${config.borderColor}`,
                      color: '#fff',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 600
                    }}
                  >
                    {config.buttonText}
                  </button>
                </div>
              </div>
            );
          })}

          {!hasActiveItems && (
            <div style={{ padding: '10px', textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
              No active items
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BagItemsCard;
